import React, { useState, useEffect } from "react";
import { deleteEvent } from "../../services/eventServices";
import { useUser } from "../../contexts/UserContext";
import { getUserById } from "../../services/userServices";
import EditEventModal from "./EditEventModal";
import ButtonEdit from "../../components/Button/ButtonEdit";
import ButtonDelete from "../../components/Button/ButtonDelete";
import ButtonCalendar from "../../components/Button/ButtonCalendar";
import { getRoleFromRoleId } from "../../utils/roleHelper";

const EventCard = ({ event, fetchEvents }) => {
  const { user } = useUser();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [eventUser, setEventUser] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);

  useEffect(() => {
    const fetchEventUser = async () => {
      try {
        if (event.user_id) {
          const fetchedUser = await getUserById(event.user_id);
          setEventUser(fetchedUser);
        }
      } catch (err) {
        console.error("Erreur lors du chargement de l’utilisateur de l’événement", err);
      }
    };

    fetchEventUser();
  }, [event.user_id]);

  const handleDeleteEvent = async () => {
    try {
      await deleteEvent(event.id);
      fetchEvents();
    } catch (error) {
      console.error("Erreur lors de la suppression de l'événement:", error);
    }
  };

  const handleEdit = () => setIsEditModalOpen(true);
  const closeEditModal = () => {
    setIsEditModalOpen(false);
    fetchEvents();
  };

  const imageUrl = event.photo_url || "/img/default_image.jpg";

  const getTypeColor = (type) => {
    switch (type) {
      case "Cours commun Club":
        return "bg-amber-500";
      case "Stage Club":
        return "bg-emerald-500";
      case "Tournoi Interclubs":
        return "bg-blue-500";
      default:
        return "bg-slate-500";
    }
  };

  const eventStartDate = new Date(event.start);
  const eventEndDate = event.end ? new Date(event.end) : null;
  const isEndDateValid = eventEndDate && !isNaN(eventEndDate);

  const userRole = user ? getRoleFromRoleId(user.role_id) : null;
  const canEditOrDelete =
    user &&
    eventUser &&
    (user.userId === eventUser.id ||
      userRole === "Administrateur" ||
      userRole === "Bureau");

  const cardBorderClass = event.is_active
    ? "border-slate-200 dark:border-slate-700"
    : "border-amber-300 dark:border-amber-400";

  return (
    <>
      <article
        className={`group flex flex-col rounded-xl overflow-hidden bg-white/90 dark:bg-slate-900/90 border ${cardBorderClass} shadow-sm hover:shadow-md transition-shadow`}
      >
        {/* Image en haut */}
        <div
          className="relative aspect-[16/9] w-full overflow-hidden cursor-pointer"
          onClick={() => setIsImageModalOpen(true)}
        >
          <img
            src={imageUrl}
            alt={event.titre}
            className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-black/0" />

          {/* Badges type + statut */}
          <div className="absolute top-2 left-2 flex flex-wrap gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2 py-1 text-[0.65rem] font-semibold uppercase tracking-wide text-white ${getTypeColor(
                event.type_event
              )}`}
            >
              {event.type_event}
            </span>
            {!event.is_active && (
              <span className="inline-flex items-center rounded-full px-2 py-1 text-[0.65rem] font-semibold uppercase tracking-wide bg-black/70 text-amber-300 border border-amber-300/70">
                En attente de validation
              </span>
            )}
          </div>

          {/* Titre dans l’image */}
          <div className="absolute bottom-2 left-2 right-2">
            <h3 className="text-sm sm:text-base font-bold text-white font-montserrat uppercase drop-shadow line-clamp-2">
              {event.titre}
            </h3>
          </div>
        </div>

        {/* Contenu */}
        <div className="flex flex-col gap-2 px-3 py-3 sm:px-4 sm:py-4 text-xs sm:text-sm text-slate-700 dark:text-slate-200">
          {/* Description courte */}
          {event.description && (
            <div
              className="prose prose-xs sm:prose-sm max-w-none text-justify text-slate-700 dark:prose-invert dark:text-slate-200 line-clamp-4"
              dangerouslySetInnerHTML={{ __html: event.description }}
            />
          )}

          {/* Dates / lieu */}
          <div className="mt-1 space-y-1">
            {!isNaN(eventStartDate) && (
              <p className="text-[0.7rem] sm:text-xs text-slate-500 dark:text-slate-400">
                <span className="font-semibold">Date de début : </span>
                {eventStartDate.toLocaleDateString("fr-FR", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            )}

            {isEndDateValid && (
              <p className="text-[0.7rem] sm:text-xs text-slate-500 dark:text-slate-400">
                <span className="font-semibold">Date de fin : </span>
                {eventEndDate.toLocaleDateString("fr-FR", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
            )}

            {event.lieu_event && (
              <p className="text-[0.7rem] sm:text-xs text-slate-500 dark:text-slate-300">
                <span className="font-semibold">Lieu : </span>
                {event.lieu_event}
              </p>
            )}
          </div>

          {/* Horaires */}
          {event.horaire_event && (
            <div
              className="mt-1 text-[0.7rem] sm:text-xs text-slate-500 dark:text-slate-300"
              dangerouslySetInnerHTML={{ __html: event.horaire_event }}
            />
          )}

          {/* Catégories */}
          {event.cate_event && (
            <div className="mt-2 flex flex-wrap gap-1">
              {event.cate_event.split(",").map((cate, index) => (
                <span
                  key={`${cate}-${index}`}
                  className="bg-slate-900 text-white text-[0.65rem] px-2 py-1 rounded-full dark:bg-slate-700"
                >
                  {cate}
                </span>
              ))}
            </div>
          )}

          {/* Actions bas de carte */}
          <div className="mt-3 flex items-center justify-between gap-2">
            <div className="flex flex-wrap gap-2 items-center">
              {event.agenda_url && (
                <a
                  href={event.agenda_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Ouvrir dans votre agenda"
                >
                  <ButtonCalendar />
                </a>
              )}
            </div>

            {canEditOrDelete && (
              <div className="flex flex-wrap gap-1">
                <ButtonEdit onClick={handleEdit} />
                <ButtonDelete onClick={handleDeleteEvent} />
              </div>
            )}
          </div>

          {/* Auteur */}
          {eventUser && (
            <div className="mt-2 text-[0.7rem] sm:text-xs text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 rounded-md px-2 py-1">
              Événement proposé par{" "}
              <span className="font-semibold">
                {eventUser.prenom} {eventUser.nom}
              </span>
            </div>
          )}
        </div>

        <EditEventModal
          isOpen={isEditModalOpen}
          onClose={closeEditModal}
          fetchEvents={fetchEvents}
          event={event}
        />
      </article>

      {/* Modal d’agrandissement de l’image */}
      {isImageModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setIsImageModalOpen(false)}
        >
          <div
            className="relative max-w-[90vw] max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setIsImageModalOpen(false)}
              className="absolute -top-3 -right-3 rounded-full bg-white text-black text-xs px-2 py-1 shadow"
            >
              Fermer
            </button>
            <img
              src={imageUrl}
              alt={event.titre}
              className="max-w-full max-h-[90vh] object-contain rounded-lg shadow-lg"
            />
          </div>
        </div>
      )}
    </>
  );
};

export default EventCard;
